import { useMemo, useCallback, useState, useEffect } from "react";
import { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";

export const usePortfolioCalculations = (
  portfolioStocks,
  searchTerm,
  sortConfig
) => {
  const [previousDayValue, setPreviousDayValue] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Memoize processed stocks
  const processedPortfolioStocks = useMemo(() => {
    let items = [...portfolioStocks];
    if (searchTerm) {
      items = items.filter(
        (s) =>
          s.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (s.datePurchased &&
            s.datePurchased.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    if (sortConfig.key) {
      items.sort((a, b) => {
        let valA, valB;

        // Handle calculated fields
        if (sortConfig.key === "costBasis") {
          valA =
            (parseFloat(a.quantity) || 0) * (parseFloat(a.costPerStock) || 0);
          valB =
            (parseFloat(b.quantity) || 0) * (parseFloat(b.costPerStock) || 0);
        } else if (sortConfig.key === "currentMarketValue") {
          valA =
            (parseFloat(a.quantity) || 0) * (parseFloat(a.currentPrice) || 0);
          valB =
            (parseFloat(b.quantity) || 0) * (parseFloat(b.currentPrice) || 0);
        } else if (sortConfig.key === "gainLoss") {
          valA =
            (parseFloat(a.quantity) || 0) *
            ((parseFloat(a.currentPrice) || 0) -
              (parseFloat(a.costPerStock) || 0));
          valB =
            (parseFloat(b.quantity) || 0) *
            ((parseFloat(b.currentPrice) || 0) -
              (parseFloat(b.costPerStock) || 0));
        } else if (sortConfig.key === "gainLossPercent") {
          const aCostBasis =
            (parseFloat(a.quantity) || 0) * (parseFloat(a.costPerStock) || 0);
          const bCostBasis =
            (parseFloat(b.quantity) || 0) * (parseFloat(b.costPerStock) || 0);
          valA =
            aCostBasis === 0
              ? 0
              : (((parseFloat(a.currentPrice) || 0) -
                  (parseFloat(a.costPerStock) || 0)) /
                  (parseFloat(a.costPerStock) || 0)) *
                100;
          valB =
            bCostBasis === 0
              ? 0
              : (((parseFloat(b.currentPrice) || 0) -
                  (parseFloat(b.costPerStock) || 0)) /
                  (parseFloat(b.costPerStock) || 0)) *
                100;
        } else if (sortConfig.key === "percentageOfPortfolio") {
          valA =
            (parseFloat(a.quantity) || 0) * (parseFloat(a.currentPrice) || 0);
          valB =
            (parseFloat(b.quantity) || 0) * (parseFloat(b.currentPrice) || 0);
        } else {
          valA = a[sortConfig.key];
          valB = b[sortConfig.key];
        }

        // Handle numeric values
        const numericKeys = [
          "quantity",
          "costPerStock",
          "currentPrice",
          "costBasis",
          "currentMarketValue",
          "gainLoss",
          "gainLossPercent",
          "percentageOfPortfolio",
        ];

        if (numericKeys.includes(sortConfig.key)) {
          valA = parseFloat(valA) || 0;
          valB = parseFloat(valB) || 0;
        } else if (sortConfig.key === "datePurchased") {
          try {
            valA = new Date(valA);
            valB = new Date(valB);
          } catch (e) {
            valA = null;
            valB = null;
          }
        } else if (typeof valA === "string" && typeof valB === "string") {
          valA = valA.toLowerCase();
          valB = valB.toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [portfolioStocks, searchTerm, sortConfig]);

  // Memoize portfolio calculations
  const calculations = useMemo(() => {
    const totalCurrentMarketValue = processedPortfolioStocks.reduce(
      (sum, s) =>
        sum + (parseFloat(s.quantity) || 0) * (parseFloat(s.currentPrice) || 0),
      0
    );

    const totalCostBasis = processedPortfolioStocks.reduce(
      (sum, s) =>
        sum + (parseFloat(s.quantity) || 0) * (parseFloat(s.costPerStock) || 0),
      0
    );

    const totalGainLoss = totalCurrentMarketValue - totalCostBasis;
    const percentageGainLoss =
      totalCostBasis === 0 ? 0 : (totalGainLoss / totalCostBasis) * 100;

    // Add percentageOfPortfolio to each stock
    const stocksWithPercentage = processedPortfolioStocks.map((stock) => {
      const currentMarketValue =
        (parseFloat(stock.quantity) || 0) *
        (parseFloat(stock.currentPrice) || 0);
      const percentageOfPortfolio =
        totalCurrentMarketValue > 0 && currentMarketValue > 0
          ? (currentMarketValue / totalCurrentMarketValue) * 100
          : 0;

      return {
        ...stock,
        percentageOfPortfolio,
      };
    });

    return {
      totalCurrentMarketValue,
      totalCostBasis,
      totalGainLoss,
      percentageGainLoss,
      processedPortfolioStocks: stocksWithPercentage,
    };
  }, [processedPortfolioStocks]);

  // Fetch previous day's closing prices
  const fetchPreviousDayPrices = useCallback(async () => {
    if (!processedPortfolioStocks.length || !API_KEYS.FMP_API_KEY) {
      return;
    }

    try {
      const symbols = processedPortfolioStocks.map((stock) => stock.symbol);
      const url = `${FMP_API_ENDPOINTS.QUOTE(symbols.join(","))}?${
        API_KEYS.FMP_API_KEY
      }`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch previous day prices: ${response.status}`
        );
      }

      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error("Invalid API response format");
      }

      const previousDayPrices = {};
      data.forEach((stock) => {
        if (stock.previousClose !== undefined) {
          previousDayPrices[stock.symbol] = stock.previousClose;
        } else {
          const currentPrice = stock.price;
          const change = stock.change;
          if (currentPrice !== undefined && change !== undefined) {
            previousDayPrices[stock.symbol] = currentPrice - change;
          }
        }
      });

      const previousValue = processedPortfolioStocks.reduce((sum, stock) => {
        const prevPrice = previousDayPrices[stock.symbol] || 0;
        const quantity = parseFloat(stock.quantity) || 0;
        return sum + quantity * prevPrice;
      }, 0);

      setPreviousDayValue(previousValue);
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching previous day prices:", error);
      setPreviousDayValue(null);
      setLastUpdated(null);
    }
  }, [processedPortfolioStocks]);

  // Calculate day over day change
  const dayOverDayChange = useMemo(() => {
    if (!previousDayValue || !calculations.totalCurrentMarketValue) {
      return {
        value: null,
        percentage: null,
        previousValue: previousDayValue || null,
        isLoading: true,
      };
    }

    const change = calculations.totalCurrentMarketValue - previousDayValue;
    const percentageChange = (change / previousDayValue) * 100;

    return {
      value: change,
      percentage: percentageChange,
      previousValue: previousDayValue,
      isLoading: false,
    };
  }, [previousDayValue, calculations.totalCurrentMarketValue]);

  // Fetch previous day prices when portfolio changes
  useEffect(() => {
    fetchPreviousDayPrices();
  }, [fetchPreviousDayPrices]);

  // Add a refresh mechanism
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchPreviousDayPrices();
    }, 5 * 60 * 1000); // Refresh every 5 minutes

    return () => clearInterval(refreshInterval);
  }, [fetchPreviousDayPrices]);

  return {
    processedPortfolioStocks:
      calculations.processedPortfolioStocks || processedPortfolioStocks,
    calculations,
    dayOverDayChange,
    lastUpdated,
    refreshDayOverDayChange: fetchPreviousDayPrices, // Expose the refresh function
  };
};
